import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Button,
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BarChartIcon from "@mui/icons-material/BarChart";
import AppsIcon from "@mui/icons-material/Apps";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import MessageIcon from "@mui/icons-material/Message";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ArticleIcon from "@mui/icons-material/Article";
import ContactsIcon from "@mui/icons-material/Contacts";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CampaignIcon from "@mui/icons-material/Campaign";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import api from "../../config/api";
import { API_ROUTES } from "../../config/apiRoutes";
import { APP_ROUTES } from "../../config/appRoutes";
import { getContactThunk } from "../../features/contact/contactThunk";
import { getQrCodeThunk } from "../../features/qrCode/qrCodeThunk";
import { getTemplateThunk } from "../../features/templates/templatesThunk";
import { getChatBotThunk } from "../../features/chatBot/chatBotThunk";
import { getCampaignsThunk } from "../../features/campaign/campaignThunk";
import { getLeadManagementThunk, getMyTaskThunk } from "../../features/leadMangement/leadMangementThunk";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import EventNoteIcon from "@mui/icons-material/EventNote";

const COLORS = {
  green: "#198754",
  orange: "#fc8006",
  blue: "#2563eb",
  purple: "#7c3aed",
  red: "#dc2626",
  amber: "#d97706",
  slate: "#64748b",
};

const DAY_MS = 24 * 60 * 60 * 1000;

const FUNNEL_META = {
  Attempted: { color: COLORS.slate, desc: "Messages queued to send" },
  Sent: { color: COLORS.blue, desc: "Accepted by WhatsApp" },
  Delivered: { color: COLORS.green, desc: "Reached the phone" },
  Read: { color: COLORS.purple, desc: "Opened by customer" },
  Failed: { color: COLORS.red, desc: "Could not be delivered" },
};

const hasAccess = (val) => val === true || val === "true" || val === 1 || val === "1";

// Backend stores unix seconds; tolerate ms timestamps and date strings too
const toMs = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isNaN(n)) return n < 1e12 ? n * 1000 : n;
  const d = Date.parse(v);
  return Number.isNaN(d) ? null : d;
};

const num = (v) => Number(v) || 0;
const asList = (v) => (Array.isArray(v) ? v : []);
const fmt = (n) => new Intl.NumberFormat("en-IN").format(n);
const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

// Small soft badge for sent / failed counts in campaign rows
const countBadge = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 0.75,
  py: "1px",
  borderRadius: 1.5,
  border: "1px solid",
  fontSize: 10.5,
  fontWeight: 700,
  lineHeight: 1.5,
  verticalAlign: "middle",
};
const badgeDot = { width: 5, height: 5, borderRadius: "50%", display: "inline-block" };

// Stable soft colour per campaign name for the initial badge
const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#db2777", "#d97706", "#198754", "#4f46e5"];
const avatarColor = (name) => {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

// Status chip colour by campaign workflow status
const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("complete") || s.includes("success")) return COLORS.green;
  if (s.includes("fail") || s.includes("cancel") || s.includes("reject")) return COLORS.red;
  if (s.includes("progress") || s.includes("running") || s.includes("sending")) return COLORS.orange;
  if (s.includes("schedul") || s.includes("pending")) return COLORS.blue;
  return COLORS.slate;
};

// "in 3 days" / "in 5 hrs" / "in 20 min" for upcoming campaigns
const timeUntil = (ms) => {
  const diff = ms - Date.now();
  if (diff <= 0) return "now";
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs} hr${hrs > 1 ? "s" : ""}`;
  const days = Math.round(hrs / 24);
  return `in ${days} day${days > 1 ? "s" : ""}`;
};

const cardSx = {
  p: { xs: 1.75, sm: 2 },
  height: "100%",
  borderRadius: 3,
  border: "1px solid #eef0f3",
  boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
};

// Section card: coloured top strip, soft corner glow, lift on hover
const sectionCard = (color) => ({
  ...cardSx,
  position: "relative",
  overflow: "hidden",
  borderTop: `3px solid ${color}`,
  background: `radial-gradient(circle at 100% 0%, ${color}10 0%, transparent 35%), #fff`,
  transition: "box-shadow .2s ease, transform .2s ease",
  "&:hover": {
    boxShadow: `0 10px 24px -8px ${color}40`,
    transform: "translateY(-2px)",
  },
});

// Small heading between dashboard rows: coloured bar + label + fading rule
const SectionLabel = ({ label, color, sx }) => (
  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 1, mb: 0.25, ...sx }}>
    <Box sx={{ width: 4, height: 18, borderRadius: 2, background: `linear-gradient(180deg, ${color}, ${color}66)` }} />
    <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#334155", letterSpacing: 0.3, whiteSpace: "nowrap" }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: "1px", background: `linear-gradient(90deg, ${color}55, transparent)` }} />
  </Stack>
);

// Labelled "View more" link-style button for card headers
const ViewMoreButton = ({ onClick, color = COLORS.green }) => (
  <Button
    size="small"
    onClick={onClick}
    endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
    sx={{
      flexShrink: 0,
      textTransform: "none",
      fontWeight: 700,
      fontSize: 12.5,
      color,
      px: 1.25,
      py: 0.4,
      borderRadius: 5,
      bgcolor: `${color}10`,
      border: `1px solid ${color}26`,
      whiteSpace: "nowrap",
      "& .MuiButton-endIcon": { ml: 0.5, transition: "transform .15s ease" },
      "&:hover": { bgcolor: color, color: "#fff", borderColor: color },
      "&:hover .MuiButton-endIcon": { transform: "translateX(2px)" },
    }}
  >
    View more
  </Button>
);

const SectionTitle = ({ title, subtitle, action, icon, color = COLORS.green }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={1}
    flexWrap="wrap"
    useFlexGap
    sx={{ pb: 1.5, mb: 2, borderBottom: "1px solid #e9edf2" }}
  >
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
      {icon && (
        <Avatar
          variant="rounded"
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            color,
            background: `linear-gradient(135deg, ${color}26, ${color}0d)`,
            border: `1px solid ${color}2e`,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 19 } })}
        </Avatar>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.25}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
    {action}
  </Stack>
);

const EmptyState = ({ text, height = 220 }) => (
  <Box
    sx={{
      height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "text.secondary",
      bgcolor: "#f8fafc",
      borderRadius: 2,
      textAlign: "center",
      px: 2,
    }}
  >
    <Typography variant="body2">{text}</Typography>
  </Box>
);

const KpiTile = ({ icon, label, value, hint, color, onClick, loading }) => (
  <Card
    onClick={onClick}
    sx={{
      position: "relative",
      overflow: "hidden",
      height: "100%",
      px: 2,
      py: 1.25,
      borderRadius: 3,
      cursor: "pointer",
      border: "1px solid #eef0f3",
      borderTop: `3px solid ${color}`,
      boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
      background: `linear-gradient(135deg, #ffffff 55%, ${color}14 100%)`,
      transition: "all .2s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: `0 8px 20px ${color}33`,
        borderColor: `${color}55`,
        borderTopColor: color,
      },
      "&:hover .kpi-icon": { bgcolor: color, color: "#fff" },
    }}
  >
    {/* Decorative corner circle */}
    <Box
      sx={{
        position: "absolute",
        right: -22,
        bottom: -22,
        width: 80,
        height: 80,
        borderRadius: "50%",
        bgcolor: `${color}12`,
      }}
    />
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: "relative" }}>
      <Avatar
        className="kpi-icon"
        variant="rounded"
        sx={{ bgcolor: `${color}1a`, color, width: 44, height: 44, borderRadius: 2.5, transition: "all .2s ease" }}
      >
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={70} height={32} />
        ) : (
          <Typography variant="h5" fontWeight={800} lineHeight={1.2}>
            {value}
          </Typography>
        )}
      </Box>
    </Stack>
    <Typography
      variant="caption"
      color="text.secondary"
      noWrap
      display="block"
      sx={{ position: "relative", mt: 1, pt: 1, borderTop: "1px dashed #e5e7eb" }}
    >
      {loading ? <Skeleton width={110} /> : hint}
    </Typography>
  </Card>
);

const DashboardSection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authentication || {});
  const contacts = asList(useSelector((s) => s.contacts?.getContacts));
  const qrCodes = asList(useSelector((s) => s.qrCode?.qrCodes));
  const templates = asList(useSelector((s) => s.templates?.getTemplates));
  const chatBots = asList(useSelector((s) => s.chatBot?.getChatBotDetails));
  const campaigns = asList(useSelector((s) => s.campaigns?.getcampaigns));
  const leads = asList(useSelector((s) => s.leadManagement?.getLeadManagementLeads));
  const myTasks = asList(useSelector((s) => s.leadManagement?.getMyTaskDetails));

  // Per-module "finished loading" flags so one slow API doesn't block the whole page
  const [done, setDone] = useState({});
  const [chatStats, setChatStats] = useState({ total: 0, unread: 0 });
  const [campaignTab, setCampaignTab] = useState("recent");

  const moduleAccess = user?.module_access || {};
  const can = {
    messages: hasAccess(moduleAccess.messages),
    qrCode: hasAccess(moduleAccess.qrGeneration),
    templates: hasAccess(moduleAccess.templates),
    contacts: hasAccess(moduleAccess.contacts),
    chatBot: hasAccess(moduleAccess.chatbot),
    leads: hasAccess(moduleAccess.leadGeneration),
    campaigns: hasAccess(moduleAccess.outboundCampaign),
  };

  useEffect(() => {
    if (!user) return;
    const fetchChatCount = (filterType) =>
      api
        .post(API_ROUTES.WA_MESSAGE_CONTACTS, {
          employee_id: user?.user_id,
          page: 1,
          limit: 1,
          filterType,
          branch_department_ids: [],
          tag_ids: [],
          qr_code_ids: [],
          organisation_ids: [],
          search: "",
        })
        .then((res) => num(res.data?.data?.pagination?.total));

    let active = true;
    const markDone = (key) => active && setDone((d) => ({ ...d, [key]: true }));
    const run = (key, job) => Promise.resolve(job).catch(() => {}).finally(() => markDone(key));

    if (can.contacts) run("contacts", dispatch(getContactThunk()));
    if (can.qrCode) run("qrCode", dispatch(getQrCodeThunk()));
    if (can.templates) run("templates", dispatch(getTemplateThunk()));
    if (can.chatBot) run("chatBot", dispatch(getChatBotThunk()));
    if (can.campaigns) run("campaigns", dispatch(getCampaignsThunk()));
    if (can.leads) {
      run("leads", dispatch(getLeadManagementThunk()));
      run("myTasks", dispatch(getMyTaskThunk()));
    }
    if (can.messages) {
      run(
        "messages",
        Promise.all([fetchChatCount(0), fetchChatCount(1)]).then(
          ([total, unread]) => active && setChatStats({ total, unread })
        )
      );
    }

    // Safety net: never show skeletons for more than 15s
    const timer = setTimeout(() => {
      if (active)
        setDone({ contacts: true, qrCode: true, templates: true, chatBot: true, campaigns: true, messages: true, leads: true, myTasks: true });
    }, 15000);

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.user_id]);

  // A section is loading only until its own API finishes (or data is already cached)
  const isLoading = (key, list) => !done[key] && !(list && list.length);

  // ---------- Derived metrics ----------
  const stats = useMemo(() => {
    const now = Date.now();
    const monthAgo = now - 30 * DAY_MS;

    // Contacts
    const contactTimes = contacts.map((c) => toMs(c.inserted_time)).filter(Boolean);
    const newContacts30 = contactTimes.filter((t) => t >= monthAgo).length;

    // Templates
    const tplStatus = { APPROVED: 0, PENDING: 0, REJECTED: 0, OTHER: 0 };
    templates.forEach((t) => {
      const s = String(t.status || "").toUpperCase();
      if (s in tplStatus) tplStatus[s] += 1;
      else tplStatus.OTHER += 1;
    });

    // Campaigns
    const funnel = campaigns.reduce(
      (acc, c) => ({
        Attempted: acc.Attempted + num(c.Attempted),
        Sent: acc.Sent + num(c.Sent),
        Delivered: acc.Delivered + num(c.Delivered),
        Read: acc.Read + num(c.Read),
        Failed: acc.Failed + num(c.Failed),
      }),
      { Attempted: 0, Sent: 0, Delivered: 0, Read: 0, Failed: 0 }
    );
    const deliveryRate = pct(funnel.Delivered, funnel.Attempted);
    const readRate = pct(funnel.Read, funnel.Delivered);

    // QR codes
    const qrScans30 = qrCodes.reduce((s, q) => s + num(q.last30days_count), 0);
    const qrScansToday = qrCodes.reduce((s, q) => s + num(q.today_count), 0);
    const topQr = [...qrCodes]
      .sort((a, b) => num(b.overall_count) - num(a.overall_count))
      .slice(0, 6);

    return {
      newContacts30,
      tplStatus,
      funnel,
      deliveryRate,
      readRate,
      qrScans30,
      qrScansToday,
      topQr,
    };
  }, [contacts, templates, campaigns, qrCodes]);

  // ---------- KPI tiles ----------
  // ---------- Lead management metrics ----------
  const leadStats = useMemo(() => {
    const assigned = leads.filter((l) => l.assigned_emp_id).length;
    const byEmp = {};
    leads.forEach((l) => {
      if (!l.assigned_emp_id) return;
      const name = l.employee_name || `Employee ${l.assigned_emp_id}`;
      byEmp[name] = (byEmp[name] || 0) + 1;
    });
    const team = Object.entries(byEmp)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    // My-task API returns buckets: Total Leads, custom lead statuses, then follow-up buckets
    const bucket = (name) => num(myTasks.find((t) => t.status_name === name)?.status_count);
    const followUpNames = ["Total Leads", "Today Follow Ups", "Follow Ups Completed", "Follow Ups Pending"];
    const statuses = myTasks
      .filter((t) => !followUpNames.includes(t.status_name))
      .map((t) => ({ name: t.status_name, count: num(t.status_count) }));

    return {
      total: leads.length,
      assigned,
      unassigned: leads.length - assigned,
      team: team.slice(0, 5),
      teamSize: team.length,
      myTotal: bucket("Total Leads"),
      today: bucket("Today Follow Ups"),
      completed: bucket("Follow Ups Completed"),
      pending: bucket("Follow Ups Pending"),
      statuses,
    };
  }, [leads, myTasks]);

  const kpis = [
    can.contacts && {
      mod: "contacts",
      list: contacts,
      label: "Total Contacts",
      icon: <ContactsIcon />,
      color: COLORS.blue,
      value: fmt(contacts.length),
      hint: `+${fmt(stats.newContacts30)} in the last 30 days`,
      to: APP_ROUTES.CONTACT.LIST,
    },
    can.messages && {
      mod: "messages",
      label: "Unread Chats",
      icon: <MessageIcon />,
      color: COLORS.orange,
      value: fmt(chatStats.unread),
      hint: `of ${fmt(chatStats.total)} conversations`,
      to: APP_ROUTES.MESSAGES,
    },
    can.campaigns && {
      mod: "campaigns",
      list: campaigns,
      label: "Campaigns",
      icon: <CampaignIcon />,
      color: COLORS.green,
      value: fmt(campaigns.length),
      hint: `${stats.deliveryRate}% delivery rate`,
      to: APP_ROUTES.CAMPAIGN.LIST,
    },
    can.templates && {
      mod: "templates",
      list: templates,
      label: "Templates",
      icon: <ArticleIcon />,
      color: COLORS.purple,
      value: fmt(templates.length),
      hint: `${stats.tplStatus.APPROVED} approved · ${stats.tplStatus.PENDING} pending`,
      to: APP_ROUTES.TEMPLATE.LIST,
    },
    can.qrCode && {
      mod: "qrCode",
      list: qrCodes,
      label: "QR Leads · 30 days",
      icon: <QrCodeIcon />,
      color: COLORS.amber,
      value: fmt(stats.qrScans30),
      hint: `${fmt(stats.qrScansToday)} today · ${qrCodes.length} QR codes`,
      to: APP_ROUTES.QRCODE.LIST,
    },
    can.chatBot && {
      mod: "chatBot",
      list: chatBots,
      label: "Chatbots",
      icon: <SmartToyIcon />,
      color: COLORS.slate,
      value: fmt(chatBots.length),
      hint: "Automated flows live",
      to: APP_ROUTES.CHATBOT.LIST,
    },
  ].filter(Boolean);

  const modules = [
    can.messages && { title: "Messages", desc: "Reply to customers in real time", icon: <MessageIcon />, color: COLORS.orange, to: APP_ROUTES.MESSAGES },
    can.contacts && { title: "Contacts", desc: "Manage audience, tags and segments", icon: <ContactsIcon />, color: COLORS.blue, to: APP_ROUTES.CONTACT.LIST },
    can.campaigns && { title: "Outbound Campaigns", desc: "Broadcast personalised messages", icon: <CampaignIcon />, color: COLORS.green, to: APP_ROUTES.CAMPAIGN.LIST },
    can.templates && { title: "Templates", desc: "Build Meta-approved message templates", icon: <ArticleIcon />, color: COLORS.purple, to: APP_ROUTES.TEMPLATE.LIST },
    can.qrCode && { title: "QR Generation", desc: "Capture leads from print and digital", icon: <QrCodeIcon />, color: COLORS.amber, to: APP_ROUTES.QRCODE.LIST },
    can.chatBot && { title: "Chat Bot", desc: "Automate 24/7 responses", icon: <SmartToyIcon />, color: COLORS.slate, to: APP_ROUTES.CHATBOT.LIST },
    can.leads && { title: "Lead Management", desc: "Assign leads and track follow-ups", icon: <AssignmentIndIcon />, color: COLORS.blue, to: APP_ROUTES.LEADMANAGEMENT.LEADMANAGEMENT },
  ].filter(Boolean);

  const templatePie = [
    { id: 0, label: "Approved", desc: "Ready to use in campaigns", value: stats.tplStatus.APPROVED, color: COLORS.green },
    { id: 1, label: "Pending", desc: "Waiting for Meta review", value: stats.tplStatus.PENDING, color: COLORS.amber },
    { id: 2, label: "Rejected", desc: "Edit and resubmit to use", value: stats.tplStatus.REJECTED, color: COLORS.red },
    { id: 3, label: "Other", desc: "Paused, disabled or draft", value: stats.tplStatus.OTHER, color: COLORS.slate },
  ].filter((d) => d.value > 0);

  const funnelKeys = ["Attempted", "Sent", "Delivered", "Read", "Failed"];
  // Split campaigns by scheduled time: future = upcoming, past/unscheduled = recent
  const campaignLists = (() => {
    const now = Date.now();
    const when = (c) => toMs(c.scheduled_time) || toMs(c.inserted_time) || 0;
    const upcoming = campaigns.filter((c) => (toMs(c.scheduled_time) || 0) > now).sort((a, b) => when(a) - when(b));
    const recent = campaigns.filter((c) => (toMs(c.scheduled_time) || 0) <= now).sort((a, b) => when(b) - when(a));
    return {
      upcoming: upcoming.slice(0, 5),
      recent: recent.slice(0, 5),
      upcomingTotal: upcoming.length,
      recentTotal: recent.length,
    };
  })();

  const shownCampaigns = campaignTab === "upcoming" ? campaignLists.upcoming : campaignLists.recent;

  return (
    <Box
      sx={{
        position: "relative",
        py: 2,
        px: { xs: 1, md: 2 },
        borderRadius: 4,
        background: `radial-gradient(circle at 0% 0%, ${COLORS.green}12 0, transparent 30%),
          radial-gradient(circle at 100% 15%, #6366f114 0, transparent 30%),
          radial-gradient(circle at 50% 100%, ${COLORS.orange}0d 0, transparent 35%),
          #f8fafc`,
        "@keyframes dashFadeUp": {
          from: { opacity: 0, transform: "translateY(12px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "& .MuiCard-root": { animation: "dashFadeUp .45s ease backwards" },
        // Stagger rows slightly
        "& .MuiGrid-container > .MuiGrid-root:nth-of-type(2) .MuiCard-root": { animationDelay: ".05s" },
        "& .MuiGrid-container > .MuiGrid-root:nth-of-type(3) .MuiCard-root": { animationDelay: ".1s" },
        "& .MuiGrid-container > .MuiGrid-root:nth-of-type(4) .MuiCard-root": { animationDelay: ".15s" },
        "& .MuiGrid-container > .MuiGrid-root:nth-of-type(n+5) .MuiCard-root": { animationDelay: ".2s" },
        "@media (prefers-reduced-motion: reduce)": { "& .MuiCard-root": { animation: "none" } },
      }}
    >
      <SectionLabel label="Overview" color={COLORS.green} sx={{ mt: 0, mb: 1.5 }} />
      {/* ---------- KPI tiles ---------- */}
      <Grid container spacing={2} mb={3}>
        {kpis.map((k) => (
          <Grid key={k.label} size={{ xs: 12, sm: 6, md: 4, lg: 12 / Math.min(kpis.length, 6) }}>
            <KpiTile {...k} loading={isLoading(k.mod, k.list)} onClick={() => navigate(k.to)} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {can.leads && (
          <Grid size={12}>
            <SectionLabel label="Lead Management" color={COLORS.blue} />
          </Grid>
        )}

        {/* ---------- Lead pipeline (left) ---------- */}
        {can.leads && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={sectionCard(COLORS.blue)}>
              <SectionTitle
                icon={<AssignmentIndIcon />}
                color={COLORS.blue}
                title="Lead Pipeline"
                subtitle="How leads are distributed across your team"
                action={
                  <ViewMoreButton onClick={() => navigate(APP_ROUTES.LEADMANAGEMENT.LEADMANAGEMENT)} />
                }
              />
              {isLoading("leads", leads) ? (
                <Skeleton variant="rounded" height={210} />
              ) : leadStats.total ? (
                <>
                  {/* Summary boxes */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: { xs: 0.75, sm: 1 }, mb: 2 }}>
                    {[
                      { label: "Total Leads", value: leadStats.total, color: COLORS.slate },
                      { label: "Assigned", value: leadStats.assigned, color: COLORS.green, pctOf: leadStats.total },
                      { label: "Unassigned", value: leadStats.unassigned, color: COLORS.amber, pctOf: leadStats.total },
                    ].map((s) => (
                      <Box key={s.label} sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: `${s.color}12`, borderLeft: `3px solid ${s.color}` }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" noWrap display="block">
                          {s.label}
                        </Typography>
                        <Stack direction="row" alignItems="baseline" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                            {fmt(s.value)}
                          </Typography>
                          {s.pctOf !== undefined && (
                            <Typography variant="caption" fontWeight={700} sx={{ color: s.color }}>
                              {pct(s.value, s.pctOf)}%
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Box>

                  {/* Team distribution */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
                      Leads by team member
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {leadStats.teamSize} member{leadStats.teamSize === 1 ? "" : "s"}
                    </Typography>
                  </Stack>
                  {leadStats.team.length ? (
                    <Stack divider={<Box sx={{ borderBottom: "1px solid #e9edf2" }} />}>
                      {leadStats.team.map((m) => {
                        const mc = avatarColor(m.name);
                        return (
                          <Stack key={m.name} direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }} py={0.9}>
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: 13,
                                fontWeight: 800,
                                color: mc,
                                background: `linear-gradient(135deg, ${mc}26, ${mc}0d)`,
                                border: `1px solid ${mc}2e`,
                              }}
                            >
                              {m.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600} noWrap title={m.name} sx={{ width: { xs: 90, sm: 130 }, flexShrink: 0 }}>
                              {m.name}
                            </Typography>
                            <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: "#eef2f7", overflow: "hidden" }}>
                              <Box sx={{ width: `${pct(m.count, leadStats.team[0].count)}%`, height: "100%", borderRadius: 3, bgcolor: mc }} />
                            </Box>
                            <Typography variant="body2" fontWeight={800} sx={{ width: 44, textAlign: "right" }}>
                              {fmt(m.count)}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  ) : (
                    <EmptyState text="No leads assigned yet. Assign leads to your team to start tracking." height={120} />
                  )}
                </>
              ) : (
                <EmptyState text="No leads yet. Contacts captured from WhatsApp and QR codes appear here as leads." />
              )}
            </Card>
          </Grid>
        )}

        {/* ---------- My follow-ups (right) ---------- */}
        {can.leads && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={sectionCard(COLORS.orange)}>
              <SectionTitle
                icon={<EventNoteIcon />}
                color={COLORS.orange}
                title="My Follow-ups"
                subtitle={`${fmt(leadStats.myTotal)} leads assigned to you`}
                action={
                  <ViewMoreButton onClick={() => navigate(APP_ROUTES.LEADMANAGEMENT.MY_TASK)} />
                }
              />
              {isLoading("myTasks", myTasks) ? (
                <Skeleton variant="rounded" height={210} />
              ) : myTasks.length ? (
                <>
                  {/* Follow-up boxes */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: { xs: 0.75, sm: 1 }, mb: 2 }}>
                    {[
                      { label: "Today", value: leadStats.today, color: COLORS.blue, icon: <EventNoteIcon sx={{ fontSize: 16 }} /> },
                      { label: "Pending", value: leadStats.pending, color: COLORS.amber, icon: <ScheduleIcon sx={{ fontSize: 16 }} /> },
                      { label: "Completed", value: leadStats.completed, color: COLORS.green, icon: <AssignmentIndIcon sx={{ fontSize: 16 }} /> },
                    ].map((s) => (
                      <Box key={s.label} sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: `${s.color}12`, borderLeft: `3px solid ${s.color}` }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: s.color }}>
                          {s.icon}
                          <Typography variant="caption" fontWeight={700} color="text.secondary" noWrap>
                            {s.label}
                          </Typography>
                        </Stack>
                        <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                          {fmt(s.value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Lead status breakdown */}
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    display="block"
                    mb={1}
                    sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
                  >
                    Leads by status
                  </Typography>
                  {leadStats.statuses.length ? (
                    <Stack divider={<Box sx={{ borderBottom: "1px solid #e9edf2" }} />}>
                      {leadStats.statuses.map((s) => {
                        const c = avatarColor(s.name);
                        return (
                          <Stack key={s.name} direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }} py={0.9}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c, flexShrink: 0 }} />
                            <Typography variant="body2" fontWeight={600} noWrap title={s.name} sx={{ width: { xs: 80, sm: 130 }, flexShrink: 0 }}>
                              {s.name}
                            </Typography>
                            <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: "#eef2f7", overflow: "hidden" }}>
                              <Box sx={{ width: `${pct(s.count, leadStats.myTotal)}%`, height: "100%", borderRadius: 3, bgcolor: c }} />
                            </Box>
                            <Typography variant="body2" fontWeight={800} sx={{ width: 36, textAlign: "right" }}>
                              {fmt(s.count)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ width: 34, textAlign: "right" }}>
                              {pct(s.count, leadStats.myTotal)}%
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  ) : (
                    <EmptyState text="No lead statuses configured yet." height={120} />
                  )}
                </>
              ) : (
                <EmptyState text="No leads assigned to you yet." />
              )}
            </Card>
          </Grid>
        )}

        {can.campaigns && (
          <Grid size={12}>
            <SectionLabel label="Campaigns" color={COLORS.green} />
          </Grid>
        )}

        {/* ---------- Campaigns list (left) ---------- */}
        {can.campaigns && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ ...sectionCard(COLORS.green), p: 2 }}>
              {/* Header: title + pill tabs + view all */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ pb: 1.5, mb: 0.5, borderBottom: "1px solid #e9edf2" }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar variant="rounded" sx={{ bgcolor: `${COLORS.green}14`, color: COLORS.green, width: 32, height: 32, borderRadius: 2 }}>
                    <CampaignIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                      Campaigns
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {campaignTab === "upcoming" ? "Scheduled broadcasts going out next" : "Latest broadcasts and their read rate"}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <Box sx={{ display: "flex", p: 0.375, bgcolor: "#f1f5f9", borderRadius: 5 }}>
                    {[
                      { v: "recent", label: "Recent", n: campaignLists.recentTotal },
                      { v: "upcoming", label: "Upcoming", n: campaignLists.upcomingTotal },
                    ].map((t) => {
                      const active = campaignTab === t.v;
                      return (
                        <Box
                          key={t.v}
                          onClick={() => setCampaignTab(t.v)}
                          sx={{
                            px: 1.5,
                            py: 0.375,
                            borderRadius: 5,
                            cursor: "pointer",
                            fontSize: 12.5,
                            fontWeight: 700,
                            userSelect: "none",
                            transition: "all .15s ease",
                            color: active ? "#fff" : "text.secondary",
                            bgcolor: active ? COLORS.green : "transparent",
                            boxShadow: active ? "0 2px 6px rgba(25,135,84,0.3)" : "none",
                          }}
                        >
                          {t.label} · {t.n}
                        </Box>
                      );
                    })}
                  </Box>
                  <ViewMoreButton onClick={() => navigate(APP_ROUTES.CAMPAIGN.LIST)} />
                </Stack>
              </Stack>

              {isLoading("campaigns", campaigns) ? (
                [1, 2, 3, 4].map((i) => <Skeleton key={i} height={44} />)
              ) : shownCampaigns.length ? (
                <Stack divider={<Box sx={{ borderBottom: "1px solid #e9edf2" }} />}>
                  {shownCampaigns.map((c) => {
                    const upcoming = campaignTab === "upcoming";
                    const sc = statusColor(c.workflow_status);
                    const ac = avatarColor(c.campaign_name);
                    const rr = pct(num(c.Read), num(c.Delivered));
                    const rc = rr >= 70 ? COLORS.green : rr >= 40 ? COLORS.amber : COLORS.red;
                    const when = upcoming ? toMs(c.scheduled_time) : toMs(c.scheduled_time) || toMs(c.inserted_time);
                    return (
                      <Stack
                        key={c.id}
                        direction="row"
                        alignItems="center"
                        spacing={{ xs: 1, sm: 1.5 }}
                        onClick={() => navigate(APP_ROUTES.CAMPAIGN.LIST)}
                        sx={{
                          position: "relative",
                          pl: 2,
                          pr: 1.25,
                          py: 1,
                          cursor: "pointer",
                          transition: "background-color .15s ease",
                          // Left accent stripe in the campaign's colour
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 4,
                            top: 10,
                            bottom: 10,
                            width: 3,
                            borderRadius: 2,
                            bgcolor: `${ac}40`,
                            transition: "all .15s ease",
                          },
                          "&:hover": { bgcolor: `${ac}08` },
                          "&:hover::before": { bgcolor: ac, top: 6, bottom: 6 },
                          "&:hover .row-go": { color: ac, transform: "translateX(2px)" },
                        }}
                      >
                        {/* Initial badge with status dot */}
                        <Box sx={{ position: "relative", flexShrink: 0 }}>
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 2.5,
                              fontSize: 15,
                              fontWeight: 800,
                              color: ac,
                              background: `linear-gradient(135deg, ${ac}26, ${ac}0d)`,
                              border: `1px solid ${ac}2e`,
                            }}
                          >
                            {upcoming ? <ScheduleIcon sx={{ fontSize: 18 }} /> : String(c.campaign_name || "?").charAt(0).toUpperCase()}
                          </Avatar>
                          <Box
                            title={c.workflow_status}
                            sx={{
                              position: "absolute",
                              right: -3,
                              bottom: -3,
                              width: 11,
                              height: 11,
                              borderRadius: "50%",
                              bgcolor: sc,
                              border: "2px solid #fff",
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap lineHeight={1.35} title={c.campaign_name}>
                            {c.campaign_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display="block"
                            lineHeight={1.35}
                            title={upcoming && c.include ? c.include : undefined}
                          >
                            {when
                              ? upcoming
                                ? new Date(when).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                                : new Date(when).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                              : "-"}
                            {upcoming ? (
                              c.include ? ` · ${String(c.include).split(",").join(", ")}` : ""
                            ) : (
                              <>
                                <Box component="span" title="Messages sent" sx={{ ...countBadge, ml: 1, color: COLORS.blue, bgcolor: `${COLORS.blue}12`, borderColor: `${COLORS.blue}26` }}>
                                  <Box component="span" sx={{ ...badgeDot, bgcolor: COLORS.blue }} />
                                  {fmt(num(c.Sent))} sent
                                </Box>
                                {num(c.Failed) > 0 && (
                                  <Box component="span" title="Messages failed" sx={{ ...countBadge, ml: 0.5, color: COLORS.red, bgcolor: `${COLORS.red}10`, borderColor: `${COLORS.red}26` }}>
                                    <Box component="span" sx={{ ...badgeDot, bgcolor: COLORS.red }} />
                                    {fmt(num(c.Failed))} failed
                                  </Box>
                                )}
                              </>
                            )}
                          </Typography>
                        </Box>
                        {/* Status: dot + text, no background */}
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: { xs: 76, sm: 96 }, flexShrink: 0 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: sc, flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap title={c.workflow_status}>
                            {c.workflow_status || (upcoming ? "Scheduled" : "-")}
                          </Typography>
                        </Stack>
                        {/* Right metric: read-rate ring (recent) / countdown (upcoming) */}
                        {upcoming ? (
                          <Box sx={{ width: 60, flexShrink: 0, textAlign: "right", display: { xs: "none", sm: "block" } }}>
                            <Typography variant="body2" fontWeight={700} lineHeight={1.2} sx={{ color: COLORS.blue }}>
                              {timeUntil(when).replace(/^in /, "")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                              to go
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            title={`Read rate ${rr}%`}
                            sx={{ position: "relative", width: 42, height: 42, flexShrink: 0, display: { xs: "none", sm: "block" } }}
                          >
                            <CircularProgress variant="determinate" value={100} size={42} thickness={3} sx={{ color: `${rc}1f`, position: "absolute" }} />
                            <CircularProgress
                              variant="determinate"
                              value={rr}
                              size={42}
                              thickness={3}
                              sx={{ color: rc, position: "absolute", "& .MuiCircularProgress-circle": { strokeLinecap: "round" } }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 4,
                                borderRadius: "50%",
                                bgcolor: `${rc}0d`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 10.5,
                                fontWeight: 800,
                                color: rc,
                              }}
                            >
                              {rr}%
                            </Box>
                          </Box>
                        )}
                        <ChevronRightIcon className="row-go" sx={{ fontSize: 18, color: "#cbd5e1", transition: "all .15s ease", display: { xs: "none", sm: "block" } }} />
                      </Stack>
                    );
                  })}
                </Stack>
              ) : (
                <EmptyState text={campaignTab === "upcoming" ? "No upcoming campaigns scheduled." : "No campaigns sent yet."} height={140} />
              )}
            </Card>
          </Grid>
        )}

        {/* ---------- Campaign performance chart (right) ---------- */}
        {can.campaigns && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={sectionCard(COLORS.purple)}>
              <SectionTitle
                icon={<BarChartIcon />}
                color={COLORS.purple}
                title="Campaign Performance"
                subtitle="Message outcomes across all campaigns"
                action={
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`Delivered ${stats.deliveryRate}%`} sx={{ bgcolor: "#e8f5ee", color: COLORS.green, fontWeight: 600 }} />
                    <Chip size="small" label={`Read ${stats.readRate}%`} sx={{ bgcolor: "#eef2ff", color: COLORS.blue, fontWeight: 600 }} />
                  </Stack>
                }
              />
              {isLoading("campaigns", campaigns) ? (
                <Skeleton variant="rounded" height={225} />
              ) : stats.funnel.Attempted ? (
                <>
                  {/* Stage counts above the chart */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))", sm: "repeat(5, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" },
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    {funnelKeys.map((k) => (
                      <Box
                        key={k}
                        title={FUNNEL_META[k].desc}
                        sx={{
                          px: 1.25,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: `${FUNNEL_META[k].color}12`,
                          borderLeft: `3px solid ${FUNNEL_META[k].color}`,
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} color="text.secondary" noWrap display="block">
                          {k}
                        </Typography>
                        <Stack direction="row" alignItems="baseline" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                            {fmt(stats.funnel[k])}
                          </Typography>
                          {k !== "Attempted" && (
                            <Typography variant="caption" fontWeight={700} sx={{ color: FUNNEL_META[k].color }}>
                              {pct(stats.funnel[k], stats.funnel.Attempted)}%
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                  <BarChart
                    height={210}
                    layout="horizontal"
                    yAxis={[
                      {
                        scaleType: "band",
                        data: funnelKeys,
                        width: 80,
                        colorMap: {
                          type: "ordinal",
                          values: funnelKeys,
                          colors: funnelKeys.map((k) => FUNNEL_META[k].color),
                        },
                      },
                    ]}
                    series={[{ data: funnelKeys.map((k) => stats.funnel[k]), label: "Messages" }]}
                    barLabel={(item) => fmt(item.value ?? 0)}
                    hideLegend
                    borderRadius={6}
                    grid={{ vertical: true }}
                    margin={{ left: 10, right: 20, top: 10, bottom: 10 }}
                    sx={{ "& .MuiBarLabel-root": { fill: "#fff", fontWeight: 700, fontSize: 13 } }}
                  />
                </>
              ) : (
                <EmptyState text="No campaign messages sent yet. Launch your first campaign to see delivery stats." />
              )}
            </Card>
          </Grid>
        )}

        {(can.templates || can.qrCode) && (
          <Grid size={12}>
            <SectionLabel label="Templates & QR Codes" color={COLORS.purple} />
          </Grid>
        )}

        {/* ---------- Template status ---------- */}
        {can.templates && (
          <Grid size={{ xs: 12, md: can.qrCode ? 6 : 12 }}>
            <Card sx={sectionCard("#0891b2")}>
              <SectionTitle icon={<ArticleIcon />} color={"#0891b2"} title="Template Status"
                subtitle="Meta approval overview"
                action={<ViewMoreButton onClick={() => navigate(APP_ROUTES.TEMPLATE.LIST)} />}
              />
              {isLoading("templates", templates) ? (
                <Skeleton variant="circular" width={200} height={200} sx={{ mx: "auto" }} />
              ) : templatePie.length ? (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems="center"
                  spacing={{ xs: 2, sm: 3 }}
                  sx={{ minHeight: 205 }}
                >
                  <Box sx={{ position: "relative", width: 185, height: 185, flexShrink: 0 }}>
                    <PieChart
                      width={185}
                      height={185}
                      hideLegend
                      margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                      series={[
                        {
                          data: templatePie,
                          innerRadius: 54,
                          outerRadius: 88,
                          paddingAngle: 2,
                          cornerRadius: 4,
                          arcLabel: (item) => fmt(item.value),
                          arcLabelMinAngle: 14,
                          highlightScope: { fade: "global", highlight: "item" },
                        },
                      ]}
                      sx={{ "& .MuiPieArcLabel-root": { fill: "#fff", fontWeight: 700, fontSize: 14 } }}
                    />
                    {/* Total in the donut centre */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <Typography variant="h4" fontWeight={800} lineHeight={1}>
                        {fmt(templates.length)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total templates
                      </Typography>
                    </Box>
                  </Box>
                  {/* Status breakdown beside the donut */}
                  <Stack spacing={1.25} sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                    {templatePie.map((d) => (
                      <Stack
                        key={d.id}
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                        sx={{ p: 1.25, borderRadius: 2, bgcolor: `${d.color}0f`, borderLeft: `3px solid ${d.color}` }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} lineHeight={1.3}>
                            {d.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {d.desc}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="body1" fontWeight={800} lineHeight={1.2}>
                            {fmt(d.value)}
                          </Typography>
                          <Typography variant="caption" fontWeight={600} sx={{ color: d.color }}>
                            {pct(d.value, templates.length)}%
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              ) : (
                <EmptyState text="No templates yet. Create one to start messaging customers outside the 24-hour window." />
              )}
            </Card>
          </Grid>
        )}

        {/* ---------- Top QR codes ---------- */}
        {can.qrCode && (
          <Grid size={{ xs: 12, md: can.templates ? 6 : 12 }}>
            <Card sx={sectionCard(COLORS.amber)}>
              <SectionTitle icon={<QrCodeIcon />} color={COLORS.amber} title="Top QR Codes"
                subtitle="Contacts captured per QR code"
                action={<ViewMoreButton onClick={() => navigate(APP_ROUTES.QRCODE.LIST)} />}
              />
              {isLoading("qrCode", qrCodes) ? (
                <Skeleton variant="rounded" height={225} />
              ) : stats.topQr.some((q) => num(q.overall_count) > 0) ? (
                <BarChart
                  height={225}
                  xAxis={[{ scaleType: "band", data: stats.topQr.map((q) => q.title || `QR ${q.id}`) }]}
                  series={[
                    {
                      id: "recent",
                      data: stats.topQr.map((q) => num(q.last30days_count)),
                      label: "Last 30 days",
                      color: "#6366f1",
                      stack: "a",
                    },
                    {
                      id: "earlier",
                      data: stats.topQr.map((q) => Math.max(num(q.overall_count) - num(q.last30days_count), 0)),
                      label: "Earlier",
                      color: "#a5b4fc",
                      stack: "a",
                    },
                  ]}
                  // One label per bar showing the QR's total; small segments get no label so nothing overlaps
                  barLabel={(item) => {
                    const q = stats.topQr[item.dataIndex];
                    const total = num(q?.overall_count);
                    const earlier = Math.max(total - num(q?.last30days_count), 0);
                    if (item.seriesId === "earlier") return earlier > 0 ? fmt(total) : null;
                    return earlier === 0 && total > 0 ? fmt(total) : null;
                  }}
                  borderRadius={6}
                  grid={{ horizontal: true }}
                  margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
                  sx={{ "& .MuiBarLabel-root": { fill: "#1e1b4b", fontWeight: 700, fontSize: 12 } }}
                  slotProps={{ legend: { direction: "horizontal", position: { vertical: "top", horizontal: "end" } } }}
                />
              ) : (
                <EmptyState text="No QR scans yet. Print or share your QR codes to capture leads." />
              )}
            </Card>
          </Grid>
        )}

        {modules.length > 0 && (
          <Grid size={12}>
            <SectionLabel label="Quick Access" color="#6366f1" />
          </Grid>
        )}

        {/* ---------- Module shortcuts ---------- */}
        {modules.length > 0 && (
          <Grid size={12}>
            <Card
              sx={{
                ...cardSx,
                position: "relative",
                overflow: "hidden",
                border: "1px solid #e4e9f2",
                // Soft tinted background with a subtle dot pattern
                background: `radial-gradient(#cbd5e1 1px, transparent 1px) 0 0 / 18px 18px,
                  linear-gradient(135deg, #f8fafc 0%, #eef2ff 55%, #f5f3ff 100%)`,
                // Multi-colour top strip made from the module colours
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  // A gradient needs 2+ colours, so repeat the colour when only one module is visible
                  background: `linear-gradient(90deg, ${[...modules, ...modules].slice(0, Math.max(modules.length, 2)).map((m) => m.color).join(", ")})`,
                },
                // Soft glow in the top-right corner
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: -80,
                  right: -60,
                  width: 220,
                  height: 220,
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(99,102,241,0.12), transparent 70%)",
                  pointerEvents: "none",
                },
              }}
            >
              <SectionTitle
                icon={<AppsIcon />}
                color="#6366f1"
                title="Your Modules"
                subtitle="Jump straight into your work"
                action={
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.4,
                      borderRadius: 5,
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#4f46e5",
                      bgcolor: "#fff",
                      border: "1px solid #c7d2fe",
                      boxShadow: "0 2px 6px -2px rgba(79,70,229,0.25)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {modules.length} module{modules.length === 1 ? "" : "s"} available
                  </Box>
                }
              />
              {/* All modules in a single row (wraps only on small screens) */}
              <Box
                sx={{
                  display: "grid",
                  // phones/small tablets: wrapping grid; from md: all modules on ONE line, sharing the width (no scroll)
                  gridTemplateColumns: {
                    xs: "repeat(auto-fill, minmax(140px, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: `repeat(${modules.length}, minmax(0, 1fr))`,
                  },
                  gap: { xs: 1.5, md: 1, lg: 1.25 },
                }}
              >
                {modules.map((m) => (
                  <Box
                    key={m.title}
                    title={m.desc}
                    onClick={() => navigate(m.to)}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      px: { xs: 1.25, md: 0.75, lg: 1 },
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1, md: 0.6, lg: 0.85 },
                      borderRadius: 3,
                      cursor: "pointer",
                      color: "#fff",
                      background: `linear-gradient(135deg, ${m.color} 0%, ${m.color}cc 100%)`,
                      boxShadow: `0 6px 16px -8px ${m.color}`,
                      transition: "all .2s ease",
                      // Decorative rings
                      "&::before, &::after": {
                        content: '""',
                        position: "absolute",
                        borderRadius: "50%",
                        border: "14px solid rgba(255,255,255,0.10)",
                        transition: "transform .3s ease",
                      },
                      "&::before": { width: 64, height: 64, right: -22, top: -26, borderWidth: 10 },
                      "&::after": { width: 40, height: 40, right: 26, bottom: -26, borderWidth: 7 },
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 14px 24px -10px ${m.color}`,
                      },
                      "&:hover::before": { transform: "scale(1.15)" },
                      "&:hover .mod-arrow": { transform: "translateX(3px)", bgcolor: "#fff", color: m.color },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: { xs: 32, md: 24, lg: 28 },
                        height: { xs: 32, md: 24, lg: 28 },
                        flexShrink: 0,
                        borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.22)",
                        color: "#fff",
                        backdropFilter: "blur(4px)",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {React.cloneElement(m.icon, { sx: { fontSize: 18 } })}
                    </Avatar>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flex: 1, minWidth: 0, position: "relative", zIndex: 1 }}>
                      <Typography variant="body2" fontWeight={700} noWrap title={m.title} sx={{ color: "#fff", fontSize: { xs: 14, md: 12, lg: 13 } }}>
                        {m.title}
                      </Typography>
                      <Box
                        className="mod-arrow"
                        sx={{
                          // arrow only where there is room (phones/tablets grid, wide screens)
                          display: { xs: "flex", md: "none", xl: "flex" },
                          flexShrink: 0,
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(255,255,255,0.22)",
                          color: "#fff",
                          transition: "all .2s ease",
                        }}
                      >
                        <ArrowForwardIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default DashboardSection;
